import { useEffect, useMemo, useState } from "react";
import { IconBuilding, IconSearch } from "@tabler/icons-react";
import { Card, Divider, Group, Heading, Input, PaginadorTabla, Stack, Text, ThemeIcon } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { OrganizacionAdmin } from "@/lib/types";
import { useOrganizacionesAdminPage } from "../hooks/use-organizaciones-admin";
import { listarOrganizacionesAdmin } from "../services/organizaciones-service";
import { OrganizacionesHeader } from "./OrganizacionesHeader";
import { OrganizacionCrearForm } from "./OrganizacionCrearForm";
import { OrganizacionesTable } from "./OrganizacionesTable";

const PAGE_SIZE = 15;

/**
 * Página de administración de organizaciones (6 sociedades del grupo).
 * Búsqueda y paginado server-side (query params con debounce) · CRUD
 * inline (renombrar, dar de baja, reactivar) con confirmación para la baja.
 */
export function AdminOrganizacionesPage() {
  const [modoCrear, setModoCrear] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(1);
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);

  const filtrosServer = useMemo(
    () => (busquedaDebounced.trim() ? { busqueda: busquedaDebounced.trim() } : {}),
    [busquedaDebounced]
  );

  useEffect(() => {
    setPage(1);
  }, [busquedaDebounced]);

  const { data } = useOrganizacionesAdminPage({ ...filtrosServer, page, pageSize: PAGE_SIZE });
  const orgs = data?.items ?? [];
  const total = data?.total ?? 0;

  const obtenerTodas = async (): Promise<OrganizacionAdmin[]> => {
    const todas: OrganizacionAdmin[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarOrganizacionesAdmin({ ...filtrosServer, page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <OrganizacionesHeader total={data?.totalCatalogo ?? 0} activas={data?.totalActivas ?? 0} modoCrear={modoCrear} onIniciarCrear={() => setModoCrear(true)} />
      <Stack gap="md">
        {modoCrear ? <OrganizacionCrearForm onSalir={() => setModoCrear(false)} /> : null}
        <Card padding="lg">
          <Stack gap="md">
            <Group gap="sm" align="flex-start">
              <ThemeIcon size="md" radius="md" variant="filled" color="multiva">
                <IconBuilding size={20} stroke={1.6} />
              </ThemeIcon>
              <div>
                <Heading order={5}>Catálogo</Heading>
                <Text size="sm" c="dimmed" className="mt-0.5">
                  Estas organizaciones se usan para derivar la organización de cada solicitud
                  (RN1, F6) y para la cabecera de las pólizas SAP. Dar de baja NO borra ·
                  preserva el histórico de solicitudes (decisión 65).
                </Text>
              </div>
            </Group>
            <Divider />
            <Input
              label="Buscar"
              placeholder="nombre, RFC o sociedad SAP…"
              leftSection={<IconSearch size={14} stroke={1.7} />}
              value={busqueda}
              onChange={(e) => setBusqueda(e.currentTarget.value)}
              wrapperClassName="max-w-90"
            />
            <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
            <OrganizacionesTable organizaciones={orgs} obtenerFilas={obtenerTodas} />
            <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="organizaciones" />
          </Stack>
        </Card>
      </Stack>
    </div>
  );
}
